import { fleetApi } from '../api/fleet';
import { refusalMessage } from '../api/errors';
import { riderApi } from '../api/riders';
import { compressImageForUpload } from './compressImageToDataUrl';
import { dialCodeForIso } from './phoneCountryCodes';

/**
 * A solo rider's registration on InGo's API (the mobile plan's M2): the
 * form this page has always asked for, turned into the account the server
 * makes, then the four documents sent with the rider's own token, one after
 * another. A document that does not arrive leaves the account standing: the
 * rider sends it again from their application's screen, where the server's
 * list says what is still missing.
 */

/** The form's document ids, as the server types them (DocumentType). */
export const DOCUMENT_TYPES = {
  nid: 'identity_document',
  lic: 'driving_licence',
  vreg: 'vehicle_registration',
  pv: 'photo_with_vehicle',
};

/**
 * The number with its country's code: "0772 123 456" in Zimbabwe is
 * "+263772123456". A number already typed with a "+" is kept as typed.
 */
export function internationalPhone(countryIso, typed) {
  const raw = String(typed || '').trim();
  const digits = raw.replace(/\D/g, '');

  if (raw.startsWith('+')) return `+${digits}`;

  return `${dialCodeForIso(countryIso)}${digits.replace(/^0+/, '')}`;
}

/** "Rudo Moyo" is Rudo and Moyo; "Rudo" alone has no last name. */
export function splitName(fullName) {
  const words = String(fullName || '').trim().split(/\s+/).filter(Boolean);

  return { first_name: words[0] ?? '', last_name: words.length > 1 ? words.slice(1).join(' ') : null };
}

/** The details `POST rider/registrations` takes, from the page's form. */
export function riderDetails(form) {
  return {
    ...splitName(form.fullName),
    email: String(form.email || '').trim().toLowerCase(),
    phone: internationalPhone(form.countryIso, form.phone),
    national_id: String(form.nationalId || '').trim(),
    // The key of one of the office's types (`api/vehicles.js`), as the form holds it: no words to translate.
    vehicle_type: form.vehicleType,
    vehicle_make: String(form.vMake || '').trim(),
    vehicle_model: String(form.vModel || '').trim(),
    vehicle_plate: String(form.vPlate || '').trim(),
    vehicle_colour: String(form.vColor || '').trim(),
    referral_code: String(form.referralCode || '').trim() || null,
    // Google's token for a rider who began with "Continue with Google": the account is linked to it.
    ...(form.googleIdToken ? { google_id_token: form.googleIdToken } : {}),
    password: form.password,
    password_confirmation: form.confirm,
  };
}

/**
 * Registers, then sends each document the rider picked, then, for a rider who
 * runs a delivery company, opens it with them as its owner (InGo ADR 0032:
 * the owner is a rider like any other; their riders are added from Fleet).
 * Answers the session, the types that did not arrive, and the company:
 * opened, or why not, for the rider to open it again from Fleet. A company
 * that could not be opened leaves the account standing.
 *
 * @returns {Promise<{ session: { account: object, mustChangePassword: boolean, application: object | null }, unsent: string[], company: { opened: boolean, problem: string } | null }>}
 */
export async function registerSoloRider({ form, docFiles, licenceExpiry, company = null, rider = riderApi, fleet = fleetApi, compress = compressImageForUpload }) {
  const session = await rider.register(riderDetails(form));
  const unsent = [];

  for (const [id, type] of Object.entries(DOCUMENT_TYPES)) {
    const picked = docFiles[id];

    if (!picked?.file) {
      unsent.push(type);
      continue;
    }

    try {
      const body = await compress(picked.file, 1600, 0.8);
      await rider.sendDocument(type, body, type === 'driving_licence' ? licenceExpiry || null : null);
    } catch {
      unsent.push(type);
    }
  }

  const name = String(company?.name || '').trim();

  if (name === '') {
    return { session, unsent, company: null };
  }

  try {
    await fleet.open(name, String(company.tradingName || '').trim() || null);

    return { session, unsent, company: { opened: true, problem: '' } };
  } catch (error) {
    return { session, unsent, company: { opened: false, problem: refusalMessage(error) } };
  }
}
